import React, { useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight,
  Image as ImageIcon, Type, List as ListIcon, Link as LinkIcon, Undo2, Redo2, RemoveFormatting,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';

/**
 * Editor di testo WYSIWYG semplice basato su contentEditable + execCommand.
 * Funzionalità:
 *  - Grassetto / Corsivo / Sottolineato
 *  - Allineamento (sinistra / centro / destra)
 *  - Dimensione font (small / medium / large / x-large)
 *  - Colore testo
 *  - Lista puntata
 *  - Inserimento immagine (caricata da PC o URL)
 *  - Inserimento link
 *  - Annulla / Ripristina / Cancella formattazione
 *
 * Salva HTML come stringa.
 */
export default function RichTextEditor({ value, onChange, placeholder = 'Scrivi qui…', folder = 'rich' }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [, force] = useState(0);

  // Inizializza il contenuto solo al primo mount o quando l'editor è vuoto e arriva valore esterno
  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.innerHTML !== (value || '')) {
      // Aggiorna solo se l'editor non è attualmente focalizzato (per non sovrascrivere mentre si scrive)
      if (document.activeElement !== ref.current) {
        ref.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML || '');
    force((x) => x + 1);
  };

  const onInput = () => {
    onChange(ref.current?.innerHTML || '');
  };

  const insertImage = async (file) => {
    if (!file) return;
    try {
      const res = await api.uploadFile(file, folder);
      // res.url può essere: URL assoluto (http), path assoluto (/api/uploads/...), o rel path (uploads/...)
      let url;
      if (res.url.startsWith('http')) url = res.url;
      else if (res.url.startsWith('/')) url = res.url;
      else if (res.url.startsWith('uploads/')) url = '/api/' + res.url;
      else if (res.url.startsWith('api/uploads/')) url = '/' + res.url;
      else url = `/api/${res.url}`;
      ref.current?.focus();
      // Inserisce l'immagine al cursore
      document.execCommand('insertHTML', false, `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />`);
      onChange(ref.current?.innerHTML || '');
      toast.success('Immagine inserita');
    } catch (e) {
      toast.error(e?.message || 'Errore upload');
    }
  };

  const insertImageUrl = () => {
    const url = window.prompt('URL immagine:');
    if (!url) return;
    document.execCommand('insertHTML', false, `<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />`);
    onChange(ref.current?.innerHTML || '');
  };

  const insertLink = () => {
    const url = window.prompt('URL link (es. https://…):');
    if (!url) return;
    exec('createLink', url);
  };

  const setSize = (size) => exec('fontSize', size); // 1..7
  const setColor = (color) => exec('foreColor', color);

  const Btn = ({ onClick, title, children, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded hover:bg-white/10 transition-colors ${active ? 'bg-pink-brand text-white' : 'text-white/80'}`}
    >
      {children}
    </button>
  );

  return (
    <div className="bg-navy-dark border border-white/15 rounded-lg overflow-hidden focus-within:border-pink-brand">
      <div className="bg-navy/60 border-b border-white/10 px-2 py-1.5 flex flex-wrap items-center gap-1">
        <Btn onClick={() => exec('bold')} title="Grassetto"><Bold size={14} /></Btn>
        <Btn onClick={() => exec('italic')} title="Corsivo"><Italic size={14} /></Btn>
        <Btn onClick={() => exec('underline')} title="Sottolineato"><UnderlineIcon size={14} /></Btn>
        <span className="w-px h-5 bg-white/15 mx-1" />
        <Btn onClick={() => exec('justifyLeft')} title="Allinea a sinistra"><AlignLeft size={14} /></Btn>
        <Btn onClick={() => exec('justifyCenter')} title="Centra"><AlignCenter size={14} /></Btn>
        <Btn onClick={() => exec('justifyRight')} title="Allinea a destra"><AlignRight size={14} /></Btn>
        <span className="w-px h-5 bg-white/15 mx-1" />
        <select
          onChange={(e) => { setSize(e.target.value); e.target.value = ''; }}
          className="bg-navy-dark border border-white/15 rounded px-2 py-1 text-white text-xs focus:outline-none"
          title="Dimensione testo"
          defaultValue=""
        >
          <option value="" disabled>Dim.</option>
          <option value="2">Piccolo</option>
          <option value="3">Normale</option>
          <option value="4">Grande</option>
          <option value="5">XL</option>
          <option value="6">XXL</option>
        </select>
        <input
          type="color"
          onChange={(e) => setColor(e.target.value)}
          className="w-7 h-7 p-0 border border-white/15 rounded cursor-pointer bg-navy-dark"
          title="Colore testo"
        />
        <span className="w-px h-5 bg-white/15 mx-1" />
        <Btn onClick={() => exec('insertUnorderedList')} title="Lista puntata"><ListIcon size={14} /></Btn>
        <Btn onClick={insertLink} title="Inserisci link"><LinkIcon size={14} /></Btn>
        <Btn onClick={() => fileRef.current?.click()} title="Inserisci immagine da PC"><ImageIcon size={14} /></Btn>
        <Btn onClick={insertImageUrl} title="Inserisci immagine da URL"><Type size={14} /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) insertImage(f); e.target.value = ''; }} />
        <span className="w-px h-5 bg-white/15 mx-1" />
        <Btn onClick={() => exec('undo')} title="Annulla"><Undo2 size={14} /></Btn>
        <Btn onClick={() => exec('redo')} title="Ripeti"><Redo2 size={14} /></Btn>
        <Btn onClick={() => exec('removeFormat')} title="Cancella formattazione"><RemoveFormatting size={14} /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        data-placeholder={placeholder}
        className="rte-content min-h-[140px] p-3 text-white text-sm focus:outline-none"
        style={{ wordBreak: 'break-word' }}
      />
    </div>
  );
}
